import React, { useState, useCallback, useMemo } from "react";
import ProfileEdit from "../homepages/ProfileEdit";
import "../home-styling/Profile.css";

const Profile = ({
  profileData,
  packages,
  loading,
  error,
  isEditingProfile,
  profileSaveLoading,
  setIsEditingProfile,
  handleSaveProfile,
  loadProfileData,
  formatCurrency,
  onAddBankAccount,
  onEditBankAccount,
  onDeleteBankAccount,
  bankAccountLoading,
}) => {
  const [imageLoadError, setImageLoadError] = useState(false);
  const [showPassbookModal, setShowPassbookModal] = useState(false);
  const [passbookImageError, setPassbookImageError] = useState(false);

  // Memoized handlers
  const handleImageError = useCallback(() => {
    setImageLoadError(true);
  }, []);

  const handleImageLoad = useCallback(() => {
    setImageLoadError(false);
  }, []);

  const handleViewPassbook = useCallback(() => {
    setShowPassbookModal(true);
    setPassbookImageError(false);
  }, []);

  const handleClosePassbookModal = useCallback(() => {
    setShowPassbookModal(false);
    setPassbookImageError(false);
  }, []);

  const handlePassbookImageError = useCallback(() => {
    setPassbookImageError(true);
  }, []);

  const handleRetryImage = useCallback((imageUrl, setErrorState) => {
    setErrorState(false);
    const img = new Image();
    img.src = `${imageUrl}&refresh=${Date.now()}`;
  }, []);

  // Memoized components
  const VerificationBadge = useCallback(({ isVerified }) => (
    <span 
      className={`verification-badge ${isVerified ? 'verified' : 'not-verified'}`}
      title={isVerified ? "Verified Operator" : "Not Verified"}
    >
      {isVerified ? "Verified" : "Not Verified"}
    </span>
  ), []);

  const StatusBadge = useCallback(({ status, type }) => (
    <span className={`status-badge ${type}`}>
      {status}
    </span>
  ), []);

  const PassbookModal = useCallback(() => {
    if (!showPassbookModal) return null;

    const bankAccount = profileData?.bankAccount;
    const passbookImageUrl = bankAccount?.passbook_image_url;

    return (
      <div className="modal-overlay" onClick={handleClosePassbookModal}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>Bank Passbook Image</h3>
            <button 
              className="modal-close" 
              onClick={handleClosePassbookModal}
            >
              ×
            </button>
          </div>
          <div className="modal-body">
            {!passbookImageError && passbookImageUrl ? (
              <div className="passbook-image-container">
                <img
                  src={passbookImageUrl}
                  alt="Bank Passbook"
                  className="passbook-image-large"
                  onError={handlePassbookImageError}
                />
              </div>
            ) : (
              <div className="image-error-message">
                <div className="error-icon">🖼️</div>
                <h4>Unable to Load Passbook Image</h4>
                <p>The passbook image could not be loaded.</p>
                <button
                  onClick={() => handleRetryImage(passbookImageUrl, setPassbookImageError)}
                  className="btn-retry"
                >
                  Retry Loading
                </button>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button 
              className="btn-secondary" 
              onClick={handleClosePassbookModal}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }, [showPassbookModal, passbookImageError, profileData, handleClosePassbookModal, handlePassbookImageError, handleRetryImage]);

  // Memoized data processing
  const processedProfileData = useMemo(() => {
    if (!profileData) return null;

    const locationCoords = profileData.location_coords;
    const hasLocationCoords = locationCoords &&
      (locationCoords.latitude || locationCoords.lat) &&
      (locationCoords.longitude || locationCoords.lng);

    const bankAccount = profileData.bankAccount;
    const hasBankAccount = bankAccount && bankAccount.account_number;
    const hasPassbookImage = bankAccount?.has_passbook_image;
    const passbookImageUrl = bankAccount?.passbook_image_url;
    const isVerified = profileData.is_verified === 1 || profileData.is_verified === true;

    return {
      ...profileData,
      hasLocationCoords,
      locationCoords,
      bankAccount,
      hasBankAccount,
      hasPassbookImage,
      passbookImageUrl,
      isVerified
    };
  }, [profileData]);

  // Memoized render sections
  const renderBrandingImage = useMemo(() => {
    const brandingImage = processedProfileData?.branding_image_url;
    if (!brandingImage) return null;

    return (
      <div className="profile-card" id="branding-image-card">
        <h2 className="card-title">Branding Image</h2>
        <div className="card-content">
          <div className="branding-image-container">
            {!imageLoadError ? (
              <img
                src={brandingImage}
                alt="Business Branding"
                className="branding-image"
                onError={handleImageError}
                onLoad={handleImageLoad}
              />
            ) : (
              <div className="image-error-message">
                <div className="error-icon">🖼️</div>
                <p>Branding image unavailable</p>
                <small>
                  The image may have expired or there may be a permissions issue.
                </small>
                <button
                  onClick={() => handleRetryImage(brandingImage, setImageLoadError)}
                  className="btn-retry"
                >
                  Retry Loading
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [processedProfileData, imageLoadError, handleImageError, handleImageLoad, handleRetryImage]);

  const renderBasicInfo = useMemo(() => {
    if (!processedProfileData) return null;

    const basicInfoFields = [
      { label: "Business Number:", value: processedProfileData.business_number, id: "businessNumber" },
      { label: "Business Name:", value: processedProfileData.name, id: "businessName" },
      { label: "Phone:", value: processedProfileData.phone, id: "phone" },
      { label: "Email:", value: processedProfileData.email, id: "email" },
      { 
        label: "Bank Details:", 
        component: <StatusBadge status={processedProfileData.hasBankAccount ? "Added" : "Not Added"} type={processedProfileData.hasBankAccount ? "added" : "not-added"} />,
        id: "bankStatus"
      },
      { 
        label: "Member Since:", 
        value: processedProfileData.created_at ? new Date(processedProfileData.created_at).toLocaleDateString() : "-",
        id: "createdAt"
      }
    ];

    return (
      <div className="profile-card">
        <h2 className="card-title">Basic Information</h2>
        <div className="card-content">
          <div className="info-grid">
            {basicInfoFields.map(({ label, value, component, id }) => (
              <div key={id} className="info-item">
                <label>{label}</label>
                {component || <span id={id} className="info-value">{value || "-"}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }, [processedProfileData, StatusBadge]);

  const renderBankAccount = useMemo(() => {
    if (!processedProfileData) return null;

    const { hasBankAccount, bankAccount } = processedProfileData;

    if (!hasBankAccount) {
      return (
        <div className="profile-card">
          <h2 className="card-title">Bank Account Information</h2>
          <div className="card-content">
            <div className="no-bank-account">
              <h3>No Bank Account Added</h3>
              <p>Please add your bank account details to receive payments.</p>
              <button
                className="btn-add-bank"
                onClick={onAddBankAccount}
                disabled={bankAccountLoading}
              >
                {bankAccountLoading ? "Loading..." : "Add Bank Account"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    const bankFields = [
      { label: "Account Holder Name:", value: bankAccount.account_holder_name, id: "accountHolderName" },
      { label: "Account Number:", value: bankAccount.account_number ? `****${bankAccount.account_number.slice(-4)}` : "-", id: "accountNumber" },
      { label: "IFSC Code:", value: bankAccount.ifsc_code, id: "ifscCode" },
      { label: "Bank Name:", value: bankAccount.bank_name, id: "bankName" },
      { label: "Branch Name:", value: bankAccount.branch_name, id: "branchName" },
      { 
        label: "Passbook Image:", 
        component: (
          <div className="passbook-section">
            <StatusBadge status={processedProfileData.hasPassbookImage ? "Uploaded" : "Missing"} type={processedProfileData.hasPassbookImage ? "uploaded" : "missing"} />
            {processedProfileData.hasPassbookImage && processedProfileData.passbookImageUrl && (
              <button className="btn-view-passbook" onClick={handleViewPassbook}>
                View Passbook
              </button>
            )}
          </div>
        ),
        id: "passbookImage"
      }
    ];

    return (
      <div className="profile-card">
        <h2 className="card-title">Bank Account Information</h2>
        <div className="card-content">
          <div className="info-grid">
            {bankFields.map(({ label, value, component, id }) => (
              <div key={id} className="info-item">
                <label>{label}</label>
                {component || <span id={id} className="info-value">{value || "-"}</span>}
              </div>
            ))}
            <div className="info-item full-width">
              <div className="bank-account-actions">
                <button
                  className="btn-edit-bank"
                  onClick={onEditBankAccount}
                  disabled={bankAccountLoading}
                >
                  {bankAccountLoading ? "Loading..." : "Update Bank Details"}
                </button>
                <button
                  className="btn-delete-bank"
                  onClick={onDeleteBankAccount}
                  disabled={bankAccountLoading}
                >
                  {bankAccountLoading ? "Deleting..." : "Delete Bank Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [processedProfileData, bankAccountLoading, onAddBankAccount, onEditBankAccount, onDeleteBankAccount, handleViewPassbook, StatusBadge]);

  const renderBusinessProfile = useMemo(() => {
    if (!processedProfileData) return null;

    const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    const { hasLocationCoords, locationCoords } = processedProfileData;

    const businessFields = [
      { label: "Agency Name:", value: processedProfileData.agency_name, fullWidth: true },
      { label: "Business Type:", value: processedProfileData.business_type, fullWidth: true },
      { label: "Address:", value: processedProfileData.address, fullWidth: true },
      { label: "Description:", value: processedProfileData.description, fullWidth: true },
      { 
        label: "Website:", 
        value: processedProfileData.website,
        component: processedProfileData.website ? (
          <a href={processedProfileData.website} target="_blank" rel="noopener noreferrer">
            {processedProfileData.website}
          </a>
        ) : "-",
        fullWidth: true
      },
      { label: "Languages:", value: processedProfileData.languages, fullWidth: true }
    ];

    const renderSocialLinks = () => {
      const socialLinks = processedProfileData.social_links;
      if (!socialLinks || (Array.isArray(socialLinks) && socialLinks.length === 0)) {
        return "-";
      }

      if (Array.isArray(socialLinks)) {
        return socialLinks.map((link, index) => (
          <div key={index} className="social-link-item">
            <a href={link.startsWith("http") ? link : `https://${link}`} target="_blank" rel="noopener noreferrer">
              {link}
            </a>
          </div>
        ));
      }

      return <div className="social-link-item">{socialLinks}</div>;
    };

    const renderLocationMap = () => {
      if (hasLocationCoords) {
        return (
          <div className="location-map-container">
            <div className="map-preview">
              <iframe
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: "8px" }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${
                  locationCoords.lat || locationCoords.latitude
                },${
                  locationCoords.lng || locationCoords.longitude
                }&zoom=15`}
                title="Business Location"
              ></iframe>
            </div>
            <div className="map-coordinates">
              <small>
                Exact coordinates:{" "}
                {locationCoords.lat || locationCoords.latitude},{" "}
                {locationCoords.lng || locationCoords.longitude}
              </small>
            </div>
          </div>
        );
      }

      if (processedProfileData.address) {
        return (
          <div className="location-map-container">
            <div className="map-preview">
              <iframe
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: "8px" }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${encodeURIComponent(
                  processedProfileData.address
                )}&zoom=15`}
                title="Business Location"
              ></iframe>
            </div>
            <div className="map-coordinates">
              <small>Location based on address</small>
            </div>
          </div>
        );
      }

      return null;
    };

    return (
      <div className="profile-card">
        <h2 className="card-title">Business Profile</h2>
        <div className="card-content">
          <div className="info-grid">
            {businessFields.map(({ label, value, component, fullWidth }, index) => (
              <div key={index} className={`info-item ${fullWidth ? 'full-width' : ''}`}>
                <label>{label}</label>
                {component || <span className="info-value">{value || "-"}</span>}
              </div>
            ))}
            
            <div className="info-item full-width">
              <label>Social Links:</label>
              <div className="social-links">
                {renderSocialLinks()}
              </div>
            </div>

            {renderLocationMap() && (
              <div className="info-item full-width">
                <label>Location Map:</label>
                {renderLocationMap()}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [processedProfileData]);

  const renderProfileHeader = useMemo(() => (
    <div className="profile-header">
      <div className="profile-title-section">
        <h2>Operator Profile</h2>
        {processedProfileData && <VerificationBadge isVerified={processedProfileData.isVerified} />}
      </div>
      <button
        className="btn-edit"
        onClick={() => setIsEditingProfile(true)}
      >
        Edit Profile
      </button>
    </div>
  ), [processedProfileData, setIsEditingProfile, VerificationBadge]);

  // Main render functions
  const renderEditMode = useMemo(() => (
    <ProfileEdit
      profileData={profileData}
      onSave={handleSaveProfile}
      onCancel={() => setIsEditingProfile(false)}
      loading={profileSaveLoading}
    />
  ), [profileData, handleSaveProfile, setIsEditingProfile, profileSaveLoading]);

  const renderLoadingState = useMemo(() => (
    <div id="loadingState" className="loading">
      <div className="spinner"></div>
      <p>Loading profile information...</p>
    </div>
  ), []);

  const renderErrorState = useMemo(() => (
    <div id="errorState" className="error-state">
      <div className="error-icon">⚠️</div>
      <h3>Unable to Load Profile</h3>
      <p>{error}</p>
      <button onClick={loadProfileData} className="btn-retry">
        Try Again
      </button>
    </div>
  ), [error, loadProfileData]);

  const renderNoDataState = useMemo(() => (
    <div className="error-state">
      <div className="error-icon">❓</div>
      <h3>No Profile Data</h3>
      <p>Profile data is not available. Please try reloading.</p>
      <button onClick={loadProfileData} className="btn-retry">
        Load Profile
      </button>
    </div>
  ), [loadProfileData]);

  const renderProfileContent = useMemo(() => (
    <div id="profileContent" className="profile-content">
      {renderProfileHeader}
      {renderBrandingImage}
      {renderBasicInfo}
      {renderBankAccount}
      {renderBusinessProfile}
      <PassbookModal />
    </div>
  ), [renderProfileHeader, renderBrandingImage, renderBasicInfo, renderBankAccount, renderBusinessProfile, PassbookModal]);

  const renderProfile = useMemo(() => {
    if (isEditingProfile) {
      return renderEditMode;
    }

    if (loading.profile) {
      return renderLoadingState;
    }

    if (error) {
      return renderErrorState;
    }

    if (!profileData) {
      return renderNoDataState;
    }

    return renderProfileContent;
  }, [
    isEditingProfile,
    loading.profile,
    error,
    profileData,
    renderEditMode,
    renderLoadingState,
    renderErrorState,
    renderNoDataState,
    renderProfileContent
  ]);

  return (
    <div className="placeholder-content">
      <div className="profile-section">{renderProfile}</div>
    </div>
  );
};

export default React.memo(Profile);